import { AnchorHTMLAttributes, Fragment, ReactNode, useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Anchor } from '@r1-frontend/ui-kit/atoms/anchor';
import { Button } from '@r1-frontend/ui-kit/atoms/button';
import { H4 } from '@r1-frontend/ui-kit/atoms/typography/heading';
import { Text } from '@r1-frontend/ui-kit/atoms/typography/text';
import { ThemedProperties } from '@r1-frontend/ui-kit/themes/types';

type TLinkProps = {
    /**
     * Пропсы для компонента ссылки, если кнопка должна вести себя как ссылка
     */
    linkProps: AnchorHTMLAttributes<HTMLAnchorElement>,

    /**
     * Компонент ссылки, если кнопка должна вести себя как ссылка
     */
    LinkComponent?: (props: AnchorHTMLAttributes<HTMLAnchorElement>) => JSX.Element,
} | {
    /**
     * Пропсы для компонента ссылки, если кнопка должна вести себя как ссылка
     */
    linkProps?: undefined,

    /**
     * Компонент ссылки, если кнопка должна вести себя как ссылка
     */
    LinkComponent?: never,
}

export type TSurveyBannerProps = TLinkProps & {
    /**
     * Изображение баннера
     */
    image: string,

    /**
     * Заголовок баннера
     */
    title: string,

    /**
     * Описание баннера
     */
    description: string,

    /**
     * Текст кнопки
     */
    buttonText: string,

    /**
     * Коллбэк, срабатывающий на нажатие кнопки
     */
    onClick?: () => void,
}

/**
 * Баннер, побуждающий пользователя принять участие в опросе.
 */
const SurveyBanner = styled(({
    image,
    title,
    description,
    buttonText,
    LinkComponent = Anchor,
    linkProps,
    onClick,
    ...props
}: TSurveyBannerProps) => {
    const ButtonWrapper = useMemo(() => {
        return linkProps
            ? ({ children }: { children: ReactNode }) => <LinkComponent {...linkProps}>{children}</LinkComponent>
            : Fragment;
    }, [LinkComponent, linkProps]);

    return (
        <article {...props}>
            <header>
                <img src={image} alt='Изображение баннера'/>
            </header>
            <main>
                <section aria-label='text-container'>
                    <H4 dangerouslySetInnerHTML={{ __html: title }}/>
                    <Text $size={'md'} dangerouslySetInnerHTML={{ __html: description }}/>
                </section>
                <ButtonWrapper>
                    <Button $small $type={'primary'} onClick={onClick}>{buttonText}</Button>
                </ButtonWrapper>
            </main>
        </article>
    );
})`
    ${({ theme }: ThemedProperties) => css`
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 0 ${theme.indents.s}px ${theme.indents.s}px;
        border-radius: ${theme.radii.pixel.l}px;
        background: linear-gradient(105deg, #E1ECF1 0%, #F5F8FB 97.21%);

        header {
            max-width: 62px;
            transform: translateY(-14px);

            img {
                width: 100%;
                height: 100%;
                object-position: center;
                object-fit: contain;
            }
        }

        main {
            display: flex;
            flex-direction: column;
            gap: ${theme.indents.xs}px;

            section[aria-label="text-container"] {
                display: flex;
                flex-direction: column;
                width: 100%;
                gap: ${theme.indents.xxxs}px;
            }

            ${Button} {
                width: 100%;
            }
        }

        @media (min-width: ${theme.breakpoints.mobile}) {
            flex-direction: row;
            flex-wrap: nowrap;
            padding: ${theme.indents.s}px;
            gap: ${theme.indents.xxs}px;
            background: linear-gradient(90deg, #E0EBF0 0%, #F5F8FB 100%);

            header {
                max-width: 70px;
                transform: none;
            }

            main {
                flex-direction: row;
                flex-wrap: nowrap;
                align-items: center;
                width: 100%;

                ${Button} {
                    width: auto;
                }
            }
        }
    `};
`;

SurveyBanner.displayName = 'SurveyBanner';

export { SurveyBanner };
